import * as GAME from 'vibegame';
import { Transform, Parent, WorldTransform } from 'vibegame/transforms';

export const PositionShaker = GAME.defineComponent({
  parent: GAME.Types.eid,
  initialized: GAME.Types.ui8,
});

export const PositionShakerDriver = GAME.defineComponent({
  radius: GAME.Types.f32,
  amplitude: GAME.Types.f32,
  offsetX: GAME.Types.f32,
  offsetY: GAME.Types.f32,
  offsetZ: GAME.Types.f32,
  global: GAME.Types.ui8,
});

const shakerQuery = GAME.defineQuery([PositionShaker, Transform]);
const driverQuery = GAME.defineQuery([PositionShakerDriver, Transform]);

const PositionShakerInitSystem: GAME.System = {
  group: 'setup',
  update(state) {
    for (const eid of shakerQuery(state.world)) {
      if (PositionShaker.initialized[eid]) continue;
      PositionShaker.initialized[eid] = 1;

      const existingParent = state.hasComponent(eid, Parent) ? Parent.entity[eid] : 0;

      const shakerParent = state.createEntity();
      state.addComponent(shakerParent, Transform, {
        posX: 0,
        posY: 0,
        posZ: 0,
        scaleX: 1,
        scaleY: 1,
        scaleZ: 1,
      });

      if (existingParent) {
        state.addComponent(shakerParent, Parent, { entity: existingParent });
      }

      PositionShaker.parent[eid] = shakerParent;
      Parent.entity[eid] = shakerParent;
    }
  },
};

const PositionShakerSystem: GAME.System = {
  group: 'simulation',
  update(state) {
    const drivers = Array.from(driverQuery(state.world));

    for (const eid of shakerQuery(state.world)) {
      const parentEid = PositionShaker.parent[eid];
      if (!parentEid) continue;

      const worldX = WorldTransform.posX[eid];
      const worldY = WorldTransform.posY[eid];
      const worldZ = WorldTransform.posZ[eid];

      let totalOffsetX = 0;
      let totalOffsetY = 0;
      let totalOffsetZ = 0;

      for (const driverEid of drivers) {
        const isGlobal = PositionShakerDriver.global[driverEid];
        const amplitude = PositionShakerDriver.amplitude[driverEid];
        const offsetX = PositionShakerDriver.offsetX[driverEid];
        const offsetY = PositionShakerDriver.offsetY[driverEid];
        const offsetZ = PositionShakerDriver.offsetZ[driverEid];

        let influence = 0;
        if (isGlobal) {
          influence = amplitude;
        } else {
          const centerX = WorldTransform.posX[driverEid];
          const centerY = WorldTransform.posY[driverEid];
          const centerZ = WorldTransform.posZ[driverEid];
          const radius = PositionShakerDriver.radius[driverEid];

          const dx = worldX - centerX;
          const dy = worldY - centerY;
          const dz = worldZ - centerZ;
          const dist = Math.sqrt(dx * dx + dy * dy + dz * dz);

          influence = radius > 0 ? Math.max(0, 1 - dist / radius) * amplitude : 0;
        }

        totalOffsetX += offsetX * influence;
        totalOffsetY += offsetY * influence;
        totalOffsetZ += offsetZ * influence;
      }

      Transform.posX[parentEid] = totalOffsetX;
      Transform.posY[parentEid] = totalOffsetY;
      Transform.posZ[parentEid] = totalOffsetZ;
    }
  },
};

export const PositionShakerPlugin: GAME.Plugin = {
  components: { PositionShaker, PositionShakerDriver },
  systems: [PositionShakerInitSystem, PositionShakerSystem],
  config: {
    defaults: {
      'position-shaker': {
        parent: 0,
        initialized: 0,
      },
      'position-shaker-driver': {
        radius: 2,
        amplitude: 0,
        offsetX: 0,
        offsetY: 1,
        offsetZ: 0,
        global: 0,
      },
    },
  },
};
